import { useState, useMemo, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Shield, X, CheckCircle2 } from "lucide-react";
import { SECTIONS, visibleQuestions } from "../lib/insuranceConsultationSchema";
import QuestionCard from "../components/insuranceConsultation/QuestionCard";
import ReviewScreen from "../components/insuranceConsultation/ReviewScreen";
import { api } from "../lib/api";

const DRAFT_KEY = "nsc.insuranceConsultation.draft.v1";

const emptyAnswers = {
  tobaccoUse: [],
  insuranceGoals: [],
  dependentTypes: [],
  healthConditions: [],
  businessProtection: [],
};

const validateQuestion = (q, answers) => {
  if (!q.required) return null;
  const v = answers[q.id];

  // Multiselect
  if (q.type === "multiselect") {
    return Array.isArray(v) && v.length > 0 ? null : "Please choose at least one option.";
  }
  // Height (composite)
  if (q.type === "height") {
    if (!answers.heightFeet && answers.heightFeet !== 0) return "Enter your height.";
    return null;
  }
  // Name group
  if (q.type === "nameGroup") {
    if (!answers.firstName || !answers.lastName) return "First and last name are required.";
    return null;
  }
  // Date of birth — age check
  if (q.id === "dateOfBirth") {
    if (!v) return "Enter your date of birth.";
    const d = new Date(v);
    if (isNaN(d.getTime())) return "Enter a valid date.";
    if (d > new Date()) return "Date cannot be in the future.";
    if (q.minAge) {
      const age = Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
      if (age < q.minAge) return `A parent or legal guardian must complete this consultation for anyone under ${q.minAge}.`;
    }
    return null;
  }
  // ZIP code
  if (q.id === "zipCode") {
    return /^\d{5}$/.test(v || "") ? null : "Enter a valid 5-digit ZIP code.";
  }
  // Email
  if (q.inputType === "email") {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v || "") ? null : "Enter a valid email address.";
  }
  // Phone
  if (q.id === "phone") {
    const digits = (v || "").replace(/\D/g, "");
    return digits.length >= 10 && digits.length <= 15 ? null : "Enter a valid phone number (10+ digits).";
  }
  // Radio/text/textarea/number
  if (v === undefined || v === null || v === "") return "This field is required.";
  return null;
};

const InsuranceConsultationPage = () => {
  const navigate = useNavigate();
  const [answers, setAnswers] = useState(() => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) return { ...emptyAnswers, ...JSON.parse(saved) };
    } catch (e) {
      // localStorage unavailable — safe to ignore
    }
    return emptyAnswers;
  });
  const [step, setStep] = useState("welcome"); // welcome | question | review | submitting | error
  const [qIndex, setQIndex] = useState(0);
  const [error, setError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [ackAccuracy, setAckAccuracy] = useState(false);
  const [ackContact, setAckContact] = useState(false);
  const [honeypot, setHoneypot] = useState(""); // must remain empty
  const [showExitPrompt, setShowExitPrompt] = useState(false);

  // Persist draft
  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(answers));
    } catch (e) {
      // localStorage full or unavailable — safe to ignore
    }
  }, [answers]);

  // Visible questions (recomputed on every render — cheap, ~40 questions)
  const questions = useMemo(() => visibleQuestions(answers), [answers]);
  const currentQ = questions[qIndex];
  const progressPct = questions.length > 0 ? Math.round(((qIndex + 1) / questions.length) * 100) : 0;

  const patch = (delta) => {
    setAnswers((prev) => ({ ...prev, ...delta }));
    setError(null);
  };

  const goNext = () => {
    // Validate current visible question
    if (currentQ) {
      const err = validateQuestion(currentQ, answers);
      if (err) {
        setError(err);
        return;
      }
    }
    // If we're at the last question, move to review
    if (qIndex >= questions.length - 1) {
      setStep("review");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setQIndex((i) => i + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goBack = () => {
    setError(null);
    if (qIndex === 0) {
      setStep("welcome");
      return;
    }
    setQIndex((i) => i - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const startForm = () => {
    setStep("question");
    setQIndex(0);
  };

  const editSection = (sectionId) => {
    const idx = questions.findIndex((q) => q.section === sectionId);
    if (idx >= 0) {
      setQIndex(idx);
      setStep("question");
    }
  };

  const submit = async () => {
    setSubmitError(null);
    if (!ackAccuracy || !ackContact) {
      setSubmitError("Please acknowledge both required statements to submit.");
      return;
    }
    setStep("submitting");
    try {
      const payload = {
        ...answers,
        heightFeet: parseInt(answers.heightFeet) || 0,
        heightInches: parseInt(answers.heightInches) || 0,
        weightPounds: parseInt(answers.weightPounds) || 0,
        accuracyAcknowledgment: ackAccuracy,
        contactConsent: ackContact,
        honeypot,
        originPage: "/insurance/consultation",
      };
      const { data } = await api.post("/insurance-consultation", payload);
      localStorage.removeItem(DRAFT_KEY);
      navigate(`/insurance/thank-you?ref=${encodeURIComponent(data.referenceNumber)}`);
    } catch (e) {
      const msg = e.response?.data?.detail || "Something went wrong. Please try again.";
      setSubmitError(typeof msg === "string" ? msg : "Submission failed. Please try again.");
      setStep("review");
    }
  };

  const saveAndExit = () => {
    setShowExitPrompt(true);
  };

  const clearDraftAndExit = () => {
    localStorage.removeItem(DRAFT_KEY);
    navigate("/insurance");
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <ConsultationHeader onExit={saveAndExit} />

      {/* Progress bar (hidden on welcome) */}
      {step !== "welcome" && (
        <div className="sticky top-[70px] z-30 bg-white border-b border-slate-100">
          <div className="max-w-3xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between text-xs text-slate-500 mb-1.5">
              <span className="uppercase tracking-wider" data-testid="ic-section-label">
                {step === "review" ? "Review" : currentQ?.sectionTitle}
              </span>
              <span data-testid="ic-progress-label">
                {step === "review" ? "Almost done" : `Question ${qIndex + 1} of ${questions.length}`}
              </span>
            </div>
            <div className="h-1.5 bg-slate-100 relative overflow-hidden">
              <div
                className="h-full bg-[#C89B3C] transition-all duration-300"
                style={{ width: `${step === "review" ? 100 : progressPct}%` }}
                data-testid="ic-progress-bar"
              />
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <main className="flex-1 flex items-start justify-center px-6 py-10 md:py-16">
        {step === "welcome" && (
          <WelcomeScreen onStart={startForm} hasDraft={Object.keys(answers).length > Object.keys(emptyAnswers).length} />
        )}

        {step === "question" && currentQ && (
          <div className="w-full max-w-2xl">
            <div className="mb-8">
              <QuestionCard q={currentQ} answers={answers} onChange={patch} error={error} />
            </div>
            <NavButtons
              onBack={goBack}
              onNext={goNext}
              isFirst={qIndex === 0}
              isLast={qIndex === questions.length - 1}
            />
          </div>
        )}

        {step === "review" && (
          <div className="w-full max-w-2xl">
            <ReviewScreen answers={answers} onEditSection={editSection} />

            <div className="mt-8 space-y-4">
              <label className="flex gap-3 items-start cursor-pointer group">
                <input
                  type="checkbox"
                  checked={ackAccuracy}
                  onChange={(e) => setAckAccuracy(e.target.checked)}
                  data-testid="ic-ack-accuracy"
                  className="mt-1.5 w-5 h-5 accent-[#C89B3C] cursor-pointer flex-shrink-0"
                />
                <span className="text-sm text-slate-600 leading-relaxed">
                  I certify that the information I provided is accurate to the best of my knowledge.
                  I understand that this is an initial consultation request and not an insurance
                  application, offer of coverage, binder, or guarantee of approval. Coverage,
                  rates, and eligibility are subject to carrier underwriting and approval.
                </span>
              </label>
              {/* NOTE FOR COMPLIANCE: The contact-consent wording below is TEMPORARY.
                  Replace with final compliance-approved language before launch. */}
              <label className="flex gap-3 items-start cursor-pointer group">
                <input
                  type="checkbox"
                  checked={ackContact}
                  onChange={(e) => setAckContact(e.target.checked)}
                  data-testid="ic-ack-contact"
                  className="mt-1.5 w-5 h-5 accent-[#C89B3C] cursor-pointer flex-shrink-0"
                />
                <span className="text-sm text-slate-600 leading-relaxed">
                  I authorize Noble Insurance and its licensed representatives to contact me
                  regarding my consultation request by telephone, text message, or email using
                  the contact information I provided. Consent is not a condition of purchasing
                  insurance. Message and data rates may apply.
                </span>
              </label>

              <p className="text-xs text-slate-400 pt-2">
                See our{" "}
                <Link to="/privacy" target="_blank" className="underline hover:text-[#C89B3C]">
                  Privacy Policy
                </Link>{" "}
                and{" "}
                <Link to="/terms" target="_blank" className="underline hover:text-[#C89B3C]">
                  Terms of Use
                </Link>
                .
              </p>
            </div>

            {/* Honeypot (invisible to real users) */}
            <div style={{ position: "absolute", left: "-5000px" }} aria-hidden="true">
              <label>
                Do not fill this
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                />
              </label>
            </div>

            {submitError && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 text-red-700 text-sm">
                {submitError}
              </div>
            )}

            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-between">
              <button
                type="button"
                onClick={() => setStep("question")}
                className="text-slate-500 hover:text-[#0B2343] text-sm inline-flex items-center gap-2"
              >
                <ArrowLeft size={16} /> Back to questions
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={!ackAccuracy || !ackContact}
                data-testid="ic-submit-btn"
                className="bg-[#0B2343] text-white px-8 py-4 font-medium hover:bg-slate-800 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Submit My Consultation <ArrowRight size={18} />
              </button>
            </div>
          </div>
        )}

        {step === "submitting" && (
          <div className="text-center py-24">
            <Shield className="mx-auto text-[#C89B3C] animate-pulse" size={48} />
            <p className="mt-5 text-lg text-[#0B2343]">Submitting your consultation…</p>
          </div>
        )}
      </main>

      {/* Save & Exit prompt */}
      {showExitPrompt && (
        <ExitPromptModal
          onKeepDraft={() => {
            setShowExitPrompt(false);
            navigate("/insurance");
          }}
          onDiscard={clearDraftAndExit}
          onCancel={() => setShowExitPrompt(false)}
        />
      )}
    </div>
  );
};

// ---- Welcome screen ----
const WelcomeScreen = ({ onStart, hasDraft }) => (
  <div className="w-full max-w-2xl text-center py-8">
    <div className="w-14 h-14 bg-[#0B2343] flex items-center justify-center mx-auto mb-8">
      <Shield className="text-[#C89B3C]" size={26} />
    </div>
    <span className="text-xs font-medium tracking-[0.2em] uppercase text-[#C89B3C] mb-3 block">
      Noble Insurance · Secure Consultation
    </span>
    <h1 className="font-serif text-4xl md:text-5xl font-bold text-[#0B2343] leading-tight mb-6">
      Protect What Matters Most
    </h1>
    <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-4">
      Welcome to Noble Insurance. This consultation helps us understand your goals,
      current coverage, financial priorities, and general health so we can recommend
      insurance solutions that may fit your needs.
    </p>
    <p className="text-sm text-slate-500 leading-relaxed mb-8">
      Completing this form does not obligate you to purchase insurance. Coverage,
      eligibility, rates, and availability are subject to carrier underwriting and
      approval.
    </p>
    <div className="inline-flex items-center gap-2 bg-slate-50 px-4 py-2 mb-8 text-sm text-slate-600">
      <CheckCircle2 size={16} className="text-[#C89B3C]" />
      Estimated completion time: <strong className="text-[#0B2343]">4–6 minutes</strong>
    </div>
    <div>
      <button
        type="button"
        onClick={onStart}
        data-testid="ic-start-btn"
        className="bg-[#0B2343] text-white px-10 py-5 font-medium hover:bg-slate-800 transition-colors inline-flex items-center gap-2 text-lg"
      >
        {hasDraft ? "Resume My Consultation" : "Start My Consultation"} <ArrowRight size={20} />
      </button>
    </div>
    <p className="mt-6 text-xs text-slate-400">
      <Link to="/insurance" className="hover:text-[#C89B3C] underline">
        Return to Noble Insurance
      </Link>
    </p>
  </div>
);

// ---- Header (custom, no site nav) ----
const ConsultationHeader = ({ onExit }) => (
  <header className="sticky top-0 z-40 bg-white border-b border-slate-100">
    <div className="max-w-6xl mx-auto px-6 h-[70px] flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 bg-[#0B2343] flex items-center justify-center flex-shrink-0">
          <span className="text-[#C89B3C] font-bold text-lg font-serif">N</span>
        </div>
        <div className="hidden sm:block">
          <p className="font-serif text-sm font-bold text-[#0B2343] leading-tight">
            Noble Insurance
          </p>
          <p className="text-[10px] uppercase tracking-wider text-slate-400">
            Secure Consultation
          </p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="hidden md:flex items-center gap-2 text-xs text-slate-500">
          <Shield size={12} className="text-[#C89B3C]" /> Secure · Encrypted
        </div>
        <button
          onClick={onExit}
          data-testid="ic-exit-btn"
          className="text-slate-500 hover:text-[#0B2343] text-sm inline-flex items-center gap-1"
        >
          <X size={16} /> Save & Exit
        </button>
      </div>
    </div>
  </header>
);

// ---- Nav buttons ----
const NavButtons = ({ onBack, onNext, isFirst, isLast }) => (
  <div className="flex justify-between items-center gap-4">
    <button
      type="button"
      onClick={onBack}
      data-testid="ic-back-btn"
      className="text-slate-500 hover:text-[#0B2343] text-sm inline-flex items-center gap-2 py-3 px-2 transition-colors"
    >
      <ArrowLeft size={16} /> {isFirst ? "Welcome" : "Back"}
    </button>
    <button
      type="button"
      onClick={onNext}
      data-testid="ic-next-btn"
      className="bg-[#0B2343] text-white px-8 py-3 font-medium hover:bg-slate-800 transition-colors inline-flex items-center gap-2"
    >
      {isLast ? "Review" : "Continue"} <ArrowRight size={16} />
    </button>
  </div>
);

// ---- Exit prompt ----
const ExitPromptModal = ({ onKeepDraft, onDiscard, onCancel }) => (
  <div
    className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
    onClick={onCancel}
  >
    <div
      className="bg-white w-full max-w-sm p-6"
      onClick={(e) => e.stopPropagation()}
    >
      <h3 className="font-serif text-xl font-bold text-[#0B2343] mb-2">Save your progress?</h3>
      <p className="text-sm text-slate-600 mb-6">
        Your answers are automatically saved in your browser. You can return to this page later on the same device to continue.
      </p>
      <div className="space-y-2">
        <button
          onClick={onKeepDraft}
          data-testid="ic-exit-keep"
          className="w-full bg-[#0B2343] text-white py-3 font-medium hover:bg-slate-800 transition-colors"
        >
          Save & Exit
        </button>
        <button
          onClick={onDiscard}
          data-testid="ic-exit-discard"
          className="w-full border border-slate-200 text-slate-600 py-3 font-medium hover:bg-slate-50 transition-colors"
        >
          Discard and start over
        </button>
        <button
          onClick={onCancel}
          className="w-full text-slate-400 py-2 text-sm hover:text-slate-600"
        >
          Never mind
        </button>
      </div>
    </div>
  </div>
);

export default InsuranceConsultationPage;
